// src/hooks/useViewManager.js - FIXED OWNER ACCESS LOGIC

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../config/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

export const useViewManager = ({
  bookId,
  bookData,
  defaultView = 'public',
  customPermissionCheck = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [viewMode, setViewMode] = useState(defaultView);
  const [user, setUser] = useState(null);
  const [hasManagementAccess, setHasManagementAccess] = useState(false);
  const [hasViewAccess, setHasViewAccess] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDeniedReason, setAccessDeniedReason] = useState(null);

  // Extract access settings from bookData
  const accessLevel = bookData?.accessLevel || 'public';
  const requireAuth = bookData?.requireAuth || false;
  const authorizedUsers = bookData?.authorizedUsers || [];
  const managementAccess = bookData?.managementAccess || {
    allOrgUsers: false,
    specificEmails: []
  };
  const viewSettings = bookData?.albumSettings?.viewSettings || null;
  const baseViewAccess = bookData?.viewAccess || {};

  const viewAccess = {
    enablePublicView: viewSettings?.enablePublicView ?? baseViewAccess.enablePublicView ?? true,
    enableManagementView: viewSettings?.enableManagementView ?? baseViewAccess.enableManagementView ?? true,
    allowViewToggle: baseViewAccess.allowViewToggle ?? true,
    defaultView: viewSettings?.defaultView ?? baseViewAccess.defaultView ?? 'public',
    requireAuthForManagement: baseViewAccess.requireAuthForManagement ?? true,
    publicViewLabel: viewSettings?.publicViewLabel ?? baseViewAccess.publicViewLabel ?? 'Public View',
    managementViewLabel: viewSettings?.managementViewLabel ?? baseViewAccess.managementViewLabel ?? 'Management'
  };
  const organizationId = bookData?.organizationId;

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      setUser(authUser);
      
      if (bookData) {
        // CRITICAL: Check if user is owner FIRST - owners bypass all restrictions
        const isOwner = authUser && bookData.ownerId === authUser.uid;
        
        if (isOwner) {
          // Owner has full access to everything
          setHasViewAccess(true);
          setHasManagementAccess(true);
          setUserRole('owner');
          setAccessDeniedReason(null);
          setLoading(false);
          return;
        }

        // For non-owners, check view access
        const viewAccessResult = await checkViewAccess(authUser, bookData);
        setHasViewAccess(viewAccessResult.hasAccess);
        
        if (!viewAccessResult.hasAccess) {
          setAccessDeniedReason(viewAccessResult.reason);
          setHasManagementAccess(false);
          setUserRole(null);
          
          // Redirect to public view if trying to access management
          if (viewMode === 'management') {
            setViewMode('public');
            updateURL('public');
          }
          setLoading(false);
          return;
        }

        // Check management access for non-owners
        if (authUser) {
          const permissionChecker = customPermissionCheck || checkManagementAccess;
          const { hasAccess, role } = await permissionChecker(authUser, bookData, bookId);
          
          setHasManagementAccess(hasAccess);
          setUserRole(role);
          
          // If trying to view management without access, redirect to public
          if (viewMode === 'management' && !hasAccess) {
            setViewMode('public');
            updateURL('public');
          }
        } else {
          setHasManagementAccess(false);
          setUserRole(null);
          
          if (viewMode === 'management') {
            setViewMode('public');
            updateURL('public');
          }
        }
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [bookId, bookData, viewMode, customPermissionCheck]);

  // Check if user can VIEW the content (for non-owners)
  const checkViewAccess = async (user, book) => {
    if (!book) return { hasAccess: false, reason: 'no_book_data' };

    const accessLevel = book.accessLevel || 'public';
    const authorizedUsers = book.authorizedUsers || [];
    const published = book.published !== false;

    // OWNER CHECK - Owner can always view their content, even if unpublished
    if (user && book.ownerId === user.uid) {
      return { hasAccess: true, reason: null };
    }

    // Unpublished content is not accessible to non-owners
    if (!published) {
      return { hasAccess: false, reason: 'unpublished' };
    }

    // Public access
    if (accessLevel === 'public') {
      // Check if anonymous views are allowed
      if (book.allowAnonymousViews === false && !user) {
        return { hasAccess: false, reason: 'auth_required' };
      }
      return { hasAccess: true, reason: null };
    }

    // Password protected
    if (accessLevel === 'password') {
      // Password check should be handled by parent component
      // Hook assumes password was validated if we reach here
      return { hasAccess: true, reason: null };
    }

    // Private access - requires authentication and authorization
    if (accessLevel === 'private') {
      if (!user) {
        return { hasAccess: false, reason: 'auth_required' };
      }

      // Check if user is in authorized users list
      if (authorizedUsers.includes(user.email)) {
        return { hasAccess: true, reason: null };
      }

      // Check if user has management access (they can also view)
      const managementCheck = await checkManagementAccess(user, book, bookId);
      if (managementCheck.hasAccess) {
        return { hasAccess: true, reason: null };
      }

      return { hasAccess: false, reason: 'not_authorized' };
    }

    // Organization access
    if (accessLevel === 'organization') {
      if (!user) {
        return { hasAccess: false, reason: 'auth_required' };
      }

      // Check if user is member of organization
      if (organizationId) {
        const isMember = await checkOrganizationMembership(user, organizationId);
        if (isMember) {
          return { hasAccess: true, reason: null };
        }
      }

      return { hasAccess: false, reason: 'not_org_member' };
    }

    // Default to no access for unknown access levels
    return { hasAccess: false, reason: 'invalid_access_level' };
  };

  // Check if user has MANAGEMENT access (can edit/manage content)
  const checkManagementAccess = async (user, book, bookId) => {
    if (!user || !book) return { hasAccess: false, role: null };

    // 1. CRITICAL: Check if user is the owner FIRST
    if (book.ownerId === user.uid) {
      return { hasAccess: true, role: 'owner' };
    }

    // 2. Check management access settings
    const managementAccess = book.managementAccess || {
      allOrgUsers: false,
      specificEmails: []
    };

    // 3. Check if user's email is in specific management emails
    if (managementAccess.specificEmails?.includes(user.email)) {
      return { hasAccess: true, role: 'manager' };
    }

    // 4. Check if all org users have access and user is org member
    if (managementAccess.allOrgUsers && book.organizationId) {
      const membership = await checkOrganizationMembership(user, book.organizationId);
      if (membership) {
        return { hasAccess: true, role: 'org_manager' };
      }
    }

    // 5. For organizations: Check if user is admin/manager in org members collection
    if (book.organizationId) {
      try {
        const orgAccess = await checkOrganizationRole(user, book.organizationId);
        if (orgAccess.hasAccess) {
          return orgAccess;
        }
      } catch (error) {
        console.error('Error checking org role:', error);
      }
    }

    // 6. For institutions: Check institution-specific permissions
    if (book.templateType === 'institution') {
      try {
        const institutionAccess = await checkInstitutionAccess(user, bookId, book);
        if (institutionAccess.hasAccess) {
          return institutionAccess;
        }
      } catch (error) {
        console.error('Error checking institution access:', error);
      }
    }

    return { hasAccess: false, role: null };
  };

  // Check if user is a member of the organization
  const checkOrganizationMembership = async (user, organizationId) => {
    if (!user || !organizationId) return false;

    try {
      // Check in pb-organisation-members collection
      const membersRef = collection(db, 'pb-organisation-members');
      const q = query(
        membersRef,
        where('organisationId', '==', organizationId),
        where('userId', '==', user.uid),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking organization membership:', error);
      return false;
    }
  };

  // Check user's role in organization
  const checkOrganizationRole = async (user, organizationId) => {
    if (!user || !organizationId) return { hasAccess: false, role: null };

    try {
      const membersRef = collection(db, 'pb-organisation-members');
      const q = query(
        membersRef,
        where('organisationId', '==', organizationId),
        where('userId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const memberData = snapshot.docs[0].data();
        
        // Only certain roles can manage
        const managementRoles = ['owner', 'admin', 'manager', 'editor'];
        if (managementRoles.includes(memberData.role)) {
          return { hasAccess: true, role: memberData.role };
        }
      }

      return { hasAccess: false, role: null };
    } catch (error) {
      console.error('Error checking organisation role:', error);
      return { hasAccess: false, role: null };
    }
  };

  // Check institution-specific access
  const checkInstitutionAccess = async (user, bookId, book) => {
    if (!user || !bookId) return { hasAccess: false, role: null };

    try {
      // Check in pb-institution-staff collection
      const staffRef = collection(db, 'pb-institution-staff');
      const q = query(
        staffRef,
        where('institutionId', '==', bookId),
        where('userId', '==', user.uid),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const staffData = snapshot.docs[0].data();
        
        // Check if role has management permissions
        const managementRoles = ['principal', 'admin', 'hod', 'coordinator'];
        if (managementRoles.includes(staffData.role)) {
          return { hasAccess: true, role: staffData.role };
        }
      }

      return { hasAccess: false, role: null };
    } catch (error) {
      console.error('Error checking institution access:', error);
      return { hasAccess: false, role: null };
    }
  };

  // Toggle between views
  const toggleView = useCallback(() => {
    if (viewMode === 'public') {
      // Switching to management
      if (!viewAccess.enableManagementView) {
        console.warn('Management view is disabled for this content');
        return;
      }

      if (!user && viewAccess.requireAuthForManagement) {
        // User not authenticated - redirect to login with return URL
        const returnUrl = `${location.pathname}?view=management`;
        navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
        return;
      }
      
      if (!hasManagementAccess) {
        // User authenticated but doesn't have management access
        console.warn('User does not have management access to this content');
        return;
      }
      
      setViewMode('management');
      updateURL('management');
    } else {
      // Switching to public
      if (!viewAccess.enablePublicView) {
        console.warn('Public view is disabled for this content');
        return;
      }

      setViewMode('public');
      updateURL('public');
    }
  }, [viewMode, user, hasManagementAccess, viewAccess, navigate, location.pathname]);

  // Set specific view
  const setView = useCallback((mode) => {
    if (!['public', 'management'].includes(mode)) return;
    
    if (mode === 'management') {
      if (!viewAccess.enableManagementView) {
        console.warn('Management view is disabled for this content');
        return;
      }

      if (!user && viewAccess.requireAuthForManagement) {
        const returnUrl = `${location.pathname}?view=management`;
        navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
        return;
      }
      
      if (!hasManagementAccess) {
        console.warn('User does not have management access to this content');
        return;
      }
    } else {
      if (!viewAccess.enablePublicView) {
        console.warn('Public view is disabled for this content');
        return;
      }
    }
    
    setViewMode(mode);
    updateURL(mode);
  }, [user, hasManagementAccess, viewAccess, navigate, location.pathname]);

  // Update URL with view parameter
  const updateURL = (mode) => {
    const params = new URLSearchParams(location.search);
    params.set('view', mode);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Check if view is available for current user
  const isViewAvailable = (mode) => {
    if (mode === 'public') {
      return viewAccess.enablePublicView && hasViewAccess;
    }
    if (mode === 'management') {
      return viewAccess.enableManagementView && hasManagementAccess;
    }
    return false;
  };

  // Check if view toggle should be shown
  const canToggleView = () => {
    return (
      viewAccess.allowViewToggle &&
      viewAccess.enablePublicView &&
      viewAccess.enableManagementView &&
      hasManagementAccess &&
      hasViewAccess
    );
  };

  return {
    viewMode,
    user,
    isAuthenticated: !!user,
    hasManagementAccess,
    hasViewAccess,
    accessDeniedReason,
    userRole,
    loading,
    toggleView,
    setView,
    isViewAvailable,
    canToggleView,
    viewConfig: {
      enablePublicView: viewAccess.enablePublicView,
      enableManagementView: viewAccess.enableManagementView,
      allowViewToggle: viewAccess.allowViewToggle,
      defaultView: viewAccess.defaultView,
      requireAuthForManagement: viewAccess.requireAuthForManagement,
      publicViewLabel: viewAccess.publicViewLabel || 'Public View',
      managementViewLabel: viewAccess.managementViewLabel || 'Management'
    },
    accessConfig: {
      accessLevel,
      requireAuth,
      published: bookData?.published !== false,
      allowAnonymousViews: bookData?.allowAnonymousViews !== false
    }
  };
};